/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

export interface LocalizedUrlResult {
	/** Path-based localized URL (e.g., /en-US/about) */
	localeSlug: string;
	/** Full URL with locale handling (uses lang param if NEXT_PUBLIC_LOCALE_MODE === "never") */
	fullUrl: string;
}

/**
 * Processes a slug and locale to generate localized URLs.
 * Handles both path-based locales (/en-US/page) and query param-based locales (?lang=en-US).
 *
 * @param locale - The locale code (e.g., "en-US")
 * @param slug - The page slug (e.g., "/about" or "/home")
 * @returns Object containing both the locale slug and full URL
 */
export const buildLocalizedUrl = (locale: string, slug: string): LocalizedUrlResult => {
	// Normalize the slug - handle "/home" special case
	const normalizedSlug = slug === "/home" ? "" : slug;

	// Build path-based locale URL
	const localeSlug = `/${locale}${normalizedSlug}`;

	// Check if we should use lang param instead of path
	const enableLangParam = process.env.NEXT_PUBLIC_LOCALE_MODE === "never";
	const baseUrl = process.env.EPIC_SERVER_URL || "";

	// Default to locale slug if lang param is disabled or no base URL
	let fullUrl: string = localeSlug;

	if (enableLangParam && baseUrl) {
		// Use slug for lang param (default to "/" for home)
		const slugForLangParam = normalizedSlug || "/";

		try {
			// Try to build a proper URL object
			const url = new URL(slugForLangParam, baseUrl);
			url.searchParams.set("lang", locale);
			fullUrl = url.toString();
		} catch {
			// Fallback to manual string concatenation
			fullUrl = `${baseUrl}${slugForLangParam}?lang=${locale}`;
		}
	}

	return {
		localeSlug,
		fullUrl,
	};
};
